interface InvoiceData {
  ticketNumber: string;
  propertyName: string;
  propertyAddress: string;
  ownerName?: string | null;
  ownerEmail?: string | null;
  ownerPhone?: string | null;
  billingAddress?: string | null;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  locationDetails: string | null;
  assignedPersonnel: string | null;
  createdAt: string;
  completedAt: string | null;
  updates: Array<{
    date: string;
    personnelName: string;
    notes: string | null;
    cost: number | null;
    hoursWorked: number | null;
    materialsUsed: string | null;
  }>;
  totalCost: number;
  totalHours: number;
}

interface InvoiceSettings {
  company_name: string;
  company_address: string;
  company_phone: string | null;
  company_email: string | null;
  tax_rate: number;
  currency: string;
  invoice_prefix: string;
  notes: string | null;
}

function getCurrencySymbol(currency: string): string {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CAD: '$',
    AUD: '$',
  };
  return symbols[currency] || '$';
}

export function generateInvoiceHTML(data: InvoiceData, settings?: InvoiceSettings): string {
  const currentDate = new Date().toLocaleDateString();

  const defaultSettings: InvoiceSettings = {
    company_name: 'Property Maintenance',
    company_address: '',
    company_phone: null,
    company_email: null,
    tax_rate: 0,
    currency: 'USD',
    invoice_prefix: 'INV',
    notes: null,
  };

  const invoiceSettings = settings || defaultSettings;
  const currencySymbol = getCurrencySymbol(invoiceSettings.currency);
  const subtotal = data.totalCost;
  const taxAmount = subtotal * (invoiceSettings.tax_rate / 100);
  const total = subtotal + taxAmount;

  const billingAddress = data.billingAddress || data.propertyAddress;
  const billingName = data.ownerName || data.propertyName;

  const lineItems = data.updates
    .filter(update => update.cost || update.hoursWorked || update.materialsUsed)
    .map((update, index) => {
      const date = new Date(update.date).toLocaleDateString();
      return `
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${index + 1}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${date}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${update.personnelName}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">
            ${update.notes || ''}
            ${update.materialsUsed ? `<br><small style="color: #6b7280;">Materials: ${update.materialsUsed}</small>` : ''}
          </td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">
            ${update.hoursWorked ? update.hoursWorked.toFixed(1) : '-'}
          </td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 500;">
            ${update.cost ? `${currencySymbol}${update.cost.toFixed(2)}` : '-'}
          </td>
        </tr>
      `;
    }).join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Invoice - ${data.ticketNumber}</title>
      <style>
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          line-height: 1.6;
          color: #1f2937;
          max-width: 210mm;
          margin: 0 auto;
          padding: 20px;
          background: #f9fafb;
        }
        .invoice {
          background: white;
          padding: 40px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 40px;
          padding-bottom: 20px;
          border-bottom: 3px solid #2563eb;
        }
        .company-info h1 {
          margin: 0 0 10px 0;
          color: #2563eb;
          font-size: 28px;
          font-weight: 700;
        }
        .company-info p {
          margin: 4px 0;
          color: #6b7280;
          font-size: 14px;
        }
        .invoice-info {
          text-align: right;
        }
        .invoice-info h2 {
          margin: 0 0 10px 0;
          font-size: 32px;
          color: #1f2937;
        }
        .invoice-info p {
          margin: 4px 0;
          color: #6b7280;
          font-size: 14px;
        }
        .bill-to {
          margin-bottom: 30px;
          padding: 20px;
          background: #f9fafb;
          border-radius: 8px;
        }
        .bill-to h3 {
          margin: 0 0 10px 0;
          font-size: 14px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .bill-to .property-name {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 5px;
        }
        .bill-to .property-address {
          color: #6b7280;
          font-size: 14px;
        }
        .work-details {
          margin-bottom: 30px;
          padding: 20px;
          background: #eff6ff;
          border-left: 4px solid #2563eb;
          border-radius: 4px;
        }
        .work-details h3 {
          margin: 0 0 15px 0;
          font-size: 18px;
          color: #1f2937;
        }
        .work-details .detail-row {
          display: flex;
          margin-bottom: 8px;
        }
        .work-details .detail-label {
          font-weight: 600;
          color: #4b5563;
          width: 180px;
        }
        .work-details .detail-value {
          color: #1f2937;
          flex: 1;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
        }
        thead {
          background: #f3f4f6;
        }
        th {
          padding: 12px;
          text-align: left;
          font-weight: 600;
          color: #4b5563;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e5e7eb;
        }
        th:nth-child(5),
        th:nth-child(6) {
          text-align: right;
        }
        td {
          font-size: 14px;
        }
        .totals {
          display: flex;
          justify-content: flex-end;
        }
        .totals-box {
          width: 350px;
          padding: 20px;
          background: #f9fafb;
          border-radius: 8px;
        }
        .totals-row {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
        }
        .totals-row.total {
          border-top: 2px solid #e5e7eb;
          margin-top: 10px;
          padding-top: 15px;
          font-size: 18px;
          font-weight: 700;
          color: #1f2937;
        }
        .totals-row.total .amount {
          color: #2563eb;
        }
        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
          text-align: center;
          color: #6b7280;
          font-size: 13px;
        }
        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 9999px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .status-completed {
          background: #d1fae5;
          color: #065f46;
        }
        .status-in_progress {
          background: #dbeafe;
          color: #1e40af;
        }
        .status-open, .status-assigned {
          background: #fef3c7;
          color: #92400e;
        }
        .print-button {
          position: fixed;
          bottom: 30px;
          right: 30px;
          padding: 14px 28px;
          background: #2563eb;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: all 0.2s;
        }
        .print-button:hover {
          background: #1d4ed8;
          transform: translateY(-2px);
          box-shadow: 0 6px 8px rgba(0,0,0,0.15);
        }
      </style>
    </head>
    <body>
      <div class="invoice">
        <div class="header">
          <div class="company-info">
            <h1>${invoiceSettings.company_name}</h1>
            ${invoiceSettings.company_address ? `<p>${invoiceSettings.company_address.replace(/\n/g, '<br>')}</p>` : ''}
            ${invoiceSettings.company_phone ? `<p>Phone: ${invoiceSettings.company_phone}</p>` : ''}
            ${invoiceSettings.company_email ? `<p>Email: ${invoiceSettings.company_email}</p>` : ''}
          </div>
          <div class="invoice-info">
            <h2>INVOICE</h2>
            <p><strong>Invoice #:</strong> ${invoiceSettings.invoice_prefix}-${data.ticketNumber}</p>
            <p><strong>Date:</strong> ${currentDate}</p>
            <p><strong>Status:</strong> <span class="status-badge status-${data.status}">${data.status.replace('_', ' ')}</span></p>
          </div>
        </div>

        <div class="bill-to">
          <h3>Bill To</h3>
          <div class="property-name">${billingName}</div>
          <div class="property-address">${billingAddress}</div>
          ${data.ownerEmail ? `<p style="margin-top: 8px; color: #6b7280;">${data.ownerEmail}</p>` : ''}
          ${data.ownerPhone ? `<p style="color: #6b7280;">${data.ownerPhone}</p>` : ''}
        </div>

        <div class="work-details">
          <h3>${data.title}</h3>
          <div class="detail-row">
            <div class="detail-label">Description:</div>
            <div class="detail-value">${data.description}</div>
          </div>
          <div class="detail-row">
            <div class="detail-label">Category:</div>
            <div class="detail-value">${data.category.charAt(0).toUpperCase() + data.category.slice(1)}</div>
          </div>
          ${data.locationDetails ? `
          <div class="detail-row">
            <div class="detail-label">Location:</div>
            <div class="detail-value">${data.locationDetails}</div>
          </div>
          ` : ''}
          ${data.assignedPersonnel ? `
          <div class="detail-row">
            <div class="detail-label">Assigned Personnel:</div>
            <div class="detail-value">${data.assignedPersonnel}</div>
          </div>
          ` : ''}
          <div class="detail-row">
            <div class="detail-label">Work Started:</div>
            <div class="detail-value">${new Date(data.createdAt).toLocaleDateString()}</div>
          </div>
          ${data.completedAt ? `
          <div class="detail-row">
            <div class="detail-label">Work Completed:</div>
            <div class="detail-value">${new Date(data.completedAt).toLocaleDateString()}</div>
          </div>
          ` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th style="width: 100px;">Date</th>
              <th style="width: 140px;">Personnel</th>
              <th>Description</th>
              <th style="width: 80px;">Hours</th>
              <th style="width: 100px;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${lineItems || '<tr><td colspan="6" style="padding: 20px; text-align: center; color: #6b7280;">No billable items</td></tr>'}
          </tbody>
        </table>

        <div class="totals">
          <div class="totals-box">
            <div class="totals-row">
              <div>Total Hours:</div>
              <div><strong>${data.totalHours.toFixed(1)}</strong></div>
            </div>
            <div class="totals-row">
              <div>Subtotal:</div>
              <div><strong>${currencySymbol}${subtotal.toFixed(2)}</strong></div>
            </div>
            ${invoiceSettings.tax_rate > 0 ? `
            <div class="totals-row">
              <div>Tax (${invoiceSettings.tax_rate}%):</div>
              <div><strong>${currencySymbol}${taxAmount.toFixed(2)}</strong></div>
            </div>
            ` : ''}
            <div class="totals-row total">
              <div>Total Amount:</div>
              <div class="amount">${currencySymbol}${total.toFixed(2)}</div>
            </div>
          </div>
        </div>

        ${invoiceSettings.notes ? `
        <div style="margin-top: 30px; padding: 20px; background: #f9fafb; border-radius: 8px; border-left: 4px solid #2563eb;">
          <h4 style="margin: 0 0 10px 0; color: #1f2937; font-size: 14px;">Notes</h4>
          <p style="margin: 0; color: #6b7280; font-size: 14px; white-space: pre-wrap;">${invoiceSettings.notes}</p>
        </div>
        ` : ''}

        <div class="footer">
          <p>Thank you for your business!</p>
          <p>For questions about this invoice, please reference ticket #${data.ticketNumber}</p>
        </div>
      </div>

      <button class="print-button no-print" onclick="window.print()">Print Invoice</button>

      <script>
        // Auto-focus for printing
        window.addEventListener('afterprint', function() {
          window.close();
        });
      </script>
    </body>
    </html>
  `;
}

export function exportTicketAsInvoice(data: InvoiceData, settings?: InvoiceSettings): void {
  const html = generateInvoiceHTML(data, settings);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);

  const newWindow = window.open(url, '_blank');
  if (newWindow) {
    newWindow.addEventListener('load', () => {
      URL.revokeObjectURL(url);
    });
  } else {
    alert('Please allow popups to view the invoice');
  }
}

export type { InvoiceData, InvoiceSettings };
